import React, { memo, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import tw from 'twin.macro'
import {
  GridColumnMenuItemProps,
  GridPinnedColumnPosition,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro'
import { GSIcon } from 'src/components/shared/GSIcon'

const GridColumnMenuPinningItemWithUnpinIconComponent = ({
  colDef,
  onClick,
}: GridColumnMenuItemProps) => {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const theme = useTheme()

  const pinColumn = useCallback(
    (side: GridPinnedColumnPosition) => (event: React.MouseEvent<HTMLElement>) => {
      apiRef.current.pinColumn(colDef.field, side)
      onClick(event)
    },
    [apiRef, colDef.field, onClick],
  )

  const unpinColumn = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      apiRef.current.unpinColumn(colDef.field)
      onClick(event)
    },
    [apiRef, colDef.field, onClick],
  )

  if (!colDef) {
    return null
  }

  const pinToLeftMenuItem = (
    <MenuItem onClick={pinColumn(GridPinnedColumnPosition.LEFT)}>
      <ListItemIcon>
        <rootProps.slots.columnMenuPinLeftIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{apiRef.current.getLocaleText('pinToLeft')}</ListItemText>
    </MenuItem>
  )

  const pinToRightMenuItem = (
    <MenuItem onClick={pinColumn(GridPinnedColumnPosition.RIGHT)}>
      <ListItemIcon>
        <rootProps.slots.columnMenuPinRightIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>{apiRef.current.getLocaleText('pinToRight')}</ListItemText>
    </MenuItem>
  )

  const side = apiRef.current.isColumnPinned(colDef.field)

  if (side) {
    const otherSide =
      side === GridPinnedColumnPosition.RIGHT
        ? GridPinnedColumnPosition.LEFT
        : GridPinnedColumnPosition.RIGHT
    const label = otherSide === GridPinnedColumnPosition.RIGHT ? 'pinToRight' : 'pinToLeft'
    const Icon =
      side === GridPinnedColumnPosition.RIGHT
        ? rootProps.slots.columnMenuPinLeftIcon
        : rootProps.slots.columnMenuPinRightIcon

    return (
      <React.Fragment>
        <MenuItem onClick={pinColumn(otherSide)}>
          <ListItemIcon>
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText(label)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={unpinColumn}>
          <ListItemIcon>
            <GSIcon iconName="gs_cross_large" css={tw`w-5 h-5`} />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('unpin')}</ListItemText>
        </MenuItem>
      </React.Fragment>
    )
  }

  if (theme.direction === 'rtl') {
    return (
      <React.Fragment>
        {pinToRightMenuItem}
        {pinToLeftMenuItem}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      {pinToLeftMenuItem}
      {pinToRightMenuItem}
    </React.Fragment>
  )
}

export const GridColumnMenuPinningItemWithUnpinIcon = memo(
  GridColumnMenuPinningItemWithUnpinIconComponent,
)
